import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Gauge, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/site/ThemeToggle";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success("Sesión iniciada");
      const dest = location.state?.from || (u.role === "admin" ? "/admin" : "/portal");
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1F] flex" data-testid="login-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle testId="login-theme-toggle" />
      </div>
      <div className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12 border-r border-[#2E2E33]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#F59E0B]/5 via-transparent to-transparent" />
        <div className="relative max-w-md">
          <Link to="/" className="inline-flex items-center gap-3 mb-12" data-testid="login-logo-link">
            <div className="w-12 h-12 border border-[#F59E0B] flex items-center justify-center bg-[#F59E0B]/10">
              <Gauge className="w-6 h-6 text-[#F59E0B]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black uppercase text-lg">SGM México</div>
              <div className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase">Cumplimiento · Sector Hidrocarburos</div>
            </div>
          </Link>
          <h1 className="font-heading font-black uppercase tracking-tighter text-5xl leading-[0.95] mb-6">
            Portal de <span className="text-[#F59E0B]">Clientes</span>
          </h1>
          <p className="text-[#A1A1AA] text-lg leading-relaxed mb-8">
            Acceda al historial de facturas, documentos técnicos y reportes de cumplimiento de sus sistemas de medición.
          </p>
          <div className="space-y-3 font-mono text-xs text-[#71717A] uppercase tracking-widest">
            <div>// Acceso seguro 24/7</div>
            <div>// Descarga inmediata de comprobantes</div>
            <div>// Trazabilidad de cumplimiento</div>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#F59E0B] text-xs font-mono uppercase tracking-widest mb-8" data-testid="login-back-link">
            <ArrowLeft className="w-3 h-3" /> Volver al inicio
          </Link>
          <div className="overline mb-3">// Acceso seguro</div>
          <h2 className="font-heading font-black uppercase text-3xl mb-2">Iniciar Sesión</h2>
          <p className="text-[#A1A1AA] mb-10">Ingrese sus credenciales para acceder al portal.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                required type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-base"
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="overline block mb-2">Contraseña</label>
              <input
                required type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="input-base"
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            {error && (
              <div className="border border-[#EF4444]/40 bg-[#EF4444]/10 px-4 py-3 text-sm text-[#FCA5A5]" data-testid="login-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="login-submit">
              {loading ? "Ingresando..." : "Entrar"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-4 text-right">
            <Link to="/forgot-password" className="text-xs font-mono uppercase tracking-widest text-[#A1A1AA] hover:text-[#F59E0B]" data-testid="login-forgot-link">
              ¿Olvidó su contraseña?
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-[#2E2E33] text-sm text-[#A1A1AA]">
            ¿No tiene cuenta?{" "}
            <Link to="/register" className="text-[#F59E0B] hover:underline font-semibold" data-testid="login-to-register">
              Solicite acceso
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
